import { createSelector } from '@reduxjs/toolkit';

import type { ACCOUNTS_REDUCER_KEY } from 'app-components/flux/accounts';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors } from 'app-components/flux/categories';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransaction,
  type ScheduledTransactionType,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import type RootState from 'app-components/utils/redux-store/rootState';

type State = Pick<RootState, typeof CATEGORIES_REDUCER_KEY | typeof ACCOUNTS_REDUCER_KEY>;

export const getCanBVA = createSelector(
  [
    (_state: State, model?: ScheduledTransaction | ScheduledTransactionType) => model,
    (state: State, model?: ScheduledTransaction | ScheduledTransactionType) =>
      Boolean(model?.transaction && transactionsSelectors.getCreditAccountFromTxn(state, model.transaction)),
  ],
  (model, hasCreditAccount) => {
    if (!model?.transaction) return false;
    return Boolean(hasCreditAccount && transactionsUtils.isTransferTxn(model.transaction));
  },
);

export const getHasBVA = createSelector(
  [(_state: State, model?: ScheduledTransaction | ScheduledTransactionType) => model, getCanBVA],
  (model, canBVA) => {
    if (!model) return false;
    const { autoAdjustAmount, autoAdjustDueOn } = model || {};
    return canBVA && ((autoAdjustAmount && autoAdjustAmount !== AutoAdjustAmountEnum.none) || autoAdjustDueOn);
  },
);

export const getIsPotentialExpense = createSelector(
  [
    (_state: State, model?: ScheduledTransaction | ScheduledTransactionType) => model,
    categoriesSelectors.getIncomeCOAIds,
  ],
  (model, incomeCOAs) => {
    const txn = model?.transaction;
    if (!txn) return false;
    if (txn.amount < 0) return true;
    if (txn.coa) {
      return !incomeCOAs.has(txn.coa.id);
    }
    if (transactionsUtils.isSplitTxn(txn)) {
      return !!(
        txn.split?.items && Boolean(txn.split.items.find((item: any) => item?.coa?.id && !incomeCOAs.has(item.coa.id)))
      );
    }
    return false;
  },
);

export const getShowConnectOption = createSelector(
  [(_state: State, model?: ScheduledTransaction | ScheduledTransactionType) => model, getIsPotentialExpense, getHasBVA],
  (model, isPotentialExpense, hasBVA) => {
    if (model?.billPresentmentAccountId === '0') {
      return false;
    }
    const txn = model?.transaction;
    if (!txn) return false;
    if (model.billPresentmentAccountId) return false;
    if (model.presentmentAccountId) return false;
    if (hasBVA) return false;
    return isPotentialExpense;
  },
);

export const getShowBillTab = createSelector(
  [(_state: State, model?: ScheduledTransaction | ScheduledTransactionType) => model, getCanBVA, getIsPotentialExpense],
  (model, canBVA, isPotentialExpense) => {
    if (canBVA) return true;
    const txn = model?.transaction;
    if (!txn) return false;
    return isPotentialExpense;
  },
);

// NOTE: if adding more selectors, remember to import/export them in the slice (billPresentmentSeletors at the bottom)
